import { useEffect, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import zionDebug from '@fs/zion-debug'
import { splunk } from '@fs/zion-splunk-logger'
import { useFeatureFlag } from '@fs/zion-flags'
import { brightspotConfig, FLAGS } from '../../config'

const debug = zionDebug('rootstech')

/**
 * Execute the Apollo query and log the result to console and splunk.
 * @param source {string} - The source of the query (method name).
 * @param query {DocumentNode} - The Apollo query to execute.
 * @param options {object} - Additional options for the query.
 * @returns {{data, error, loading}} - The result of the query.
 */
export default function useLoggedQuery(source, query, options) {
  const { data, error, loading } = useQuery(query, {
    fetchPolicy: brightspotConfig.query.fetchPolicy.CACHE_FIRST, // Prefer cached data, only fetch from network if not in cache
    ...options,
  })
  const logGraphQLDetails = useFeatureFlag(FLAGS.LOG_GRAPHQL_DETAILS)

  const log = useCallback(
    (status) => {
      const dataType = typeof data
      let message = '<empty>'
      if (dataType === 'string') {
        message = data.substring(0, 200)
      } else if (data) {
        message = `expected string, got ${dataType}`
      }

      splunk.log({
        id: 'RootsTech_GraphQL',
        source,
        status,
        data: status === 'success' ? '<skipped>' : message,
        error,
      })
    },
    [source, data, error]
  )

  useEffect(() => {
    if (loading) {
      return
    }
    if ((debug.enabled || logGraphQLDetails.isOn) && !loading && !error) {
      log(`success`)
    }
    if (!loading && error) {
      log(`error`)
    }
  }, [loading, data, error, logGraphQLDetails.isOn, log])

  return { data, error, loading }
}
